import dayjs from 'dayjs';
import { Invoice } from 'types';
import { getPaymentMethodName } from 'utils/formatters';

/**
 * Human label for an invoice's payment method, matching what the Payment Method
 * column renders.
 *
 * An invoice carries either a Stripe payment method or a non-Stripe one, never
 * both. The previous implementation labelled every non-Stripe invoice
 * "Cash Or Check", so Gift Card, E Transfer, POS Terminal and Unknown all
 * exported as cash — which broke the rule that an export matches the screen.
 */
export const invoicePaymentMethodLabel = (
  invoice: Pick<Invoice, 'paymentMethod' | 'nonStripePaymentMethod'>,
): string => {
  if (invoice.paymentMethod) {
    return getPaymentMethodName(invoice.paymentMethod);
  }
  return invoice.nonStripePaymentMethod ?? '';
};

export const dataToRow = (d: Invoice): { [key: string]: any } => ({
  'Due Date': dayjs(d.dueDate).format('YYYY-MM-DD'),
  Status: d.status,
  'Last Action': d.lastAction,
  'Pre-tax Total': (d.pretaxTotal / 100).toFixed(2),
  Tax: (d.tax / 100).toFixed(2),
  Total: (d.total / 100).toFixed(2),
  'Paid Amount': d.paidAmount !== null ? (d.paidAmount / 100).toFixed(2) : '',
  Athlete: d.athlete.fullName,
  'Billing Source': d.invoiceItems
    .map(ii => (ii.quantity === 1 ? ii.productName : `(${ii.quantity}) ${ii.productName}`))
    .join(' | '),
  'Last Updated At': dayjs(d.updatedAt).format('YYYY-MM-DD'),
  'Payment Date': d.paymentDate ? dayjs(d.paymentDate).format('YYYY-MM-DD') : '',
  'Auto Pay': d.autoPay,
  'Payment Method': invoicePaymentMethodLabel(d),
});

export const csvOptions = {
  fieldSeparator: ',',
  quoteStrings: '',
  decimalSeparator: '.',
  showLabels: true,
  useBom: true,
  useKeysAsHeaders: true,
};
